import { useCallback, useEffect, useRef, useState } from "react";

const TICK_MS = 50;

export interface ProgressTimerState {
    active: boolean;
    progress: number;
    durationMs: number;
}

export interface ProgressTimerApi {
    state: ProgressTimerState;
    progressRef: React.MutableRefObject<number>;
    start: (durationMs: number) => void;
    stop: () => { elapsedMs: number; completed: boolean; durationMs: number };
    pauseFor: (ms: number) => void;
    /** Indefinite pause — caller must invoke resume() to lift it. Use
     *  for modals / confirmations where the wait is user-driven and we
     *  don't know how long it'll take. */
    pause: () => void;
    resume: () => void;
    extend: (deltaMs: number) => void;
    reset: () => void;
}

export const useProgressTimer = (): ProgressTimerApi => {
    const [active, setActive] = useState(false);
    const [progress, setProgress] = useState(0);
    const [durationMs, setDurationMs] = useState(0);

    const progressRef = useRef(0);
    const durationRef = useRef(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);
    const pausedRef = useRef(false);
    const unpauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const clearAll = () => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (unpauseTimeoutRef.current) {
            clearTimeout(unpauseTimeoutRef.current);
            unpauseTimeoutRef.current = null;
        }
        pausedRef.current = false;
    };

    const start = useCallback((ms: number) => {
        clearAll();
        progressRef.current = 0;
        durationRef.current = ms;
        setProgress(0);
        setDurationMs(ms);
        setActive(true);
        intervalRef.current = setInterval(() => {
            if (pausedRef.current) return;
            const d = durationRef.current;
            if (d <= 0) return;
            const next = progressRef.current + (TICK_MS / d) * 100;
            if (next >= 100) {
                progressRef.current = 100;
                setProgress(100);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                return;
            }
            progressRef.current = next;
            setProgress(next);
        }, TICK_MS);
    }, []);

    const stop = useCallback((): { elapsedMs: number; completed: boolean; durationMs: number } => {
        const p = progressRef.current;
        const d = durationRef.current;
        const elapsedMs = Math.min(d, (p / 100) * d);
        const completed = p >= 100;
        clearAll();
        setActive(false);
        setProgress(0);
        progressRef.current = 0;
        return { elapsedMs, completed, durationMs: d };
    }, []);

    const reset = useCallback(() => {
        clearAll();
        progressRef.current = 0;
        durationRef.current = 0;
        setProgress(0);
        setDurationMs(0);
        setActive(false);
    }, []);

    const pauseFor = useCallback((ms: number) => {
        if (!intervalRef.current || ms <= 0) return;
        pausedRef.current = true;
        if (unpauseTimeoutRef.current) clearTimeout(unpauseTimeoutRef.current);
        unpauseTimeoutRef.current = setTimeout(() => {
            pausedRef.current = false;
            unpauseTimeoutRef.current = null;
        }, ms);
    }, []);

    // Indefinite pause / resume. Cancels any pending pauseFor unpause
    // (so a "pause for 2s" already in flight doesn't fire while a modal
    // is still open) and leaves pausedRef.current=true until resume().
    const pause = useCallback(() => {
        if (!intervalRef.current) return;
        if (unpauseTimeoutRef.current) {
            clearTimeout(unpauseTimeoutRef.current);
            unpauseTimeoutRef.current = null;
        }
        pausedRef.current = true;
    }, []);

    const resume = useCallback(() => {
        if (!intervalRef.current) return;
        if (unpauseTimeoutRef.current) {
            clearTimeout(unpauseTimeoutRef.current);
            unpauseTimeoutRef.current = null;
        }
        pausedRef.current = false;
    }, []);

    const extend = useCallback((deltaMs: number) => {
        if (!intervalRef.current) return;
        const elapsedMs = (progressRef.current / 100) * durationRef.current;
        const newDuration = Math.max(elapsedMs + 100, durationRef.current + deltaMs);
        durationRef.current = newDuration;
        const newProgress = (elapsedMs / newDuration) * 100;
        progressRef.current = newProgress;
        setDurationMs(newDuration);
        setProgress(newProgress);
    }, []);

    useEffect(() => () => clearAll(), []);

    return {
        state: { active, progress, durationMs },
        progressRef,
        start,
        stop,
        pauseFor,
        pause,
        resume,
        extend,
        reset,
    };
};
